import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { X, Send, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_UNIT_OPTIONS = [
  "Townhouse (Villa) — 4 BR",
  "Grand Villa — 5 BR",
  "Twin Villa (Mansion) — 6 BR",
  "Standalone (Grand Mansion) — 6–7 BR",
  "Not sure yet — please advise",
];

const BUDGETS = ["AED 1M – 3M", "AED 3M – 5M", "AED 5M – 10M", "AED 10M+"];

export const LeadModal = ({
  open,
  onClose,
  projectName = "Tilal Binghatti",
  prefillUnit = "",
  accentColor = "#1F5C3A",
  unitOptions,
  unitLabel = "Interested in",
  headline,
  subheadline,
}) => {
  const UNIT_OPTIONS = unitOptions || DEFAULT_UNIT_OPTIONS;
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    unit: prefillUnit || UNIT_OPTIONS[0],
    budget: BUDGETS[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setSuccess(false);
      setForm((f) => ({ ...f, unit: prefillUnit || f.unit }));
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open, prefillUnit]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        budget: form.budget,
        project_interest: `${projectName} — ${form.unit}`,
        message: form.message,
      });
      // Meta Pixel — Lead conversion event
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "Lead", {
          content_name: projectName,
          content_category: form.unit,
        });
      }
      setSuccess(true);
      toast.success("Thank you — we'll be in touch within one business day.");
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || "Could not submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
      data-testid="lead-modal"
      aria-modal="true"
      role="dialog"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in"
        onClick={onClose}
      />
      <div
        className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl shadow-2xl"
        style={{ background: "#0a1a12", border: `1px solid ${accentColor}40` }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
          aria-label="Close"
          data-testid="lead-modal-close"
        >
          <X size={18} />
        </button>

        {success ? (
          <div className="p-10 md:p-14 text-center text-white">
            <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-6" style={{ background: `${accentColor}30` }}>
              <CheckCircle2 size={36} style={{ color: accentColor }} strokeWidth={1.5} />
            </div>
            <h3 className="font-serif-display text-3xl mb-3">Thank you</h3>
            <p className="text-white/70 max-w-sm mx-auto">
              Your enquiry for <span style={{ color: accentColor }}>{projectName}</span> has been received.
              A senior property consultant will contact you within one business day with the brochure,
              floor plans and current payment plan.
            </p>
            <button
              onClick={onClose}
              className="mt-8 px-8 py-3 rounded-lg text-sm font-bold tracking-[0.25em] text-white transition hover:opacity-90"
              style={{ background: accentColor }}
              data-testid="lead-modal-done"
            >
              CLOSE
            </button>
          </div>
        ) : (
          <div className="p-8 md:p-10 text-white">
            <div className="mb-8">
              <div className="text-[10px] tracking-[0.4em] uppercase mb-2" style={{ color: accentColor }}>
                Register Your Interest
              </div>
              <h3 className="font-serif-display text-3xl md:text-4xl leading-tight">
                {headline || (
                  <>
                    Get the full brochure &<br />
                    <em className="italic" style={{ color: accentColor }}>payment plan</em>
                  </>
                )}
              </h3>
              <p className="text-white/60 mt-3 text-sm">
                {subheadline || "Confidential. No obligation. Response within one business day."}
              </p>
            </div>

            <form onSubmit={submit} className="space-y-5" data-testid="lead-modal-form">
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Full name *</label>
                <input
                  required
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your full name"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[color:var(--accent)]"
                  style={{ "--accent": accentColor }}
                  data-testid="lm-name"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Phone / WhatsApp *</label>
                  <div className="lead-phone-wrapper">
                    <PhoneInput
                      country={"ae"}
                      value={form.phone}
                      onChange={(val) => setForm((f) => ({ ...f, phone: val ? `+${val}` : "" }))}
                      enableSearch
                      searchPlaceholder="Search country"
                      placeholder="5X XXX XXXX"
                      inputProps={{ required: true, "data-testid": "lm-phone" }}
                      specialLabel=""
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Email *</label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-white/40"
                    data-testid="lm-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">{unitLabel}</label>
                  <select
                    value={form.unit}
                    onChange={update("unit")}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm focus:outline-none"
                    data-testid="lm-unit"
                  >
                    {UNIT_OPTIONS.map((u) => (
                      <option key={u} value={u} className="bg-[#0a1a12]">{u}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Budget range</label>
                  <select
                    value={form.budget}
                    onChange={update("budget")}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm focus:outline-none"
                    data-testid="lm-budget"
                  >
                    {BUDGETS.map((b) => (
                      <option key={b} value={b} className="bg-[#0a1a12]">{b}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Message (optional)</label>
                <textarea
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Payment plan, floor plans, availability…"
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm resize-none focus:outline-none"
                  data-testid="lm-message"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-lg text-sm font-bold tracking-[0.3em] text-white flex items-center justify-center gap-3 transition hover:opacity-90 disabled:opacity-60"
                style={{ background: accentColor }}
                data-testid="lm-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} /> SENDING…
                  </>
                ) : (
                  <>
                    REQUEST CALLBACK <Send size={16} />
                  </>
                )}
              </button>

              <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 text-center">
                Your information is confidential
              </p>
            </form>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
